import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [siteContent, setSiteContent] = useState(() => {
    const saved = localStorage.getItem('naqd-site-content');
    return saved ? JSON.parse(saved) : null;
  });

  const [blogPosts, setBlogPosts] = useState(() => {
    const saved = localStorage.getItem('naqd-blog-posts');
    return saved ? JSON.parse(saved) : null;
  });

  const [layoutConfig, setLayoutConfig] = useState(() => {
    const saved = localStorage.getItem('naqd-layout-config');
    return saved ? JSON.parse(saved) : null;
  });

  const [images, setImages] = useState(() => {
    const saved = localStorage.getItem('naqd-images');
    return saved ? JSON.parse(saved) : {};
  });

  // Default content structure
  const defaultSiteContent = {
    hero: {
      title: "Expert Chartered Accountants in Malappuram",
      subtitle: "Transform your business with NAQD Consulting - Kerala's leading CA firm specializing in tax planning, GST compliance, and business advisory services.",
      cta: "Get Started Today",
      backgroundImage: null
    },
    about: {
      title: "About NAQD Consulting",
      description: "Leading chartered accountancy firm in Malappuram, Kerala, providing comprehensive financial and taxation services with digital-first approach and local expertise.",
      mission: "To empower businesses in Kerala with expert financial guidance and innovative solutions.",
      vision: "To be the most trusted financial partner for businesses across Kerala."
    },
    services: {
      title: "Our Services",
      subtitle: "Comprehensive financial solutions tailored for Kerala businesses"
    },
    contact: {
      address: "2nd floor, Span building, Opp. Kendra Vidyalaya, AK Road, Malappuram, Kerala 676505",
      phone: "+91 90742 86763",
      email: "info@naqd.in",
      hours: "Mon - Sat: 9:30 AM - 6:00 PM"
    },
    footer: {
      companyName: "NAQD Consulting Private Limited",
      copyright: "All rights reserved"
    }
  };

  const defaultLayoutConfig = {
    hero: { enabled: true, showCTA: true },
    services: { enabled: true, showPricing: true },
    testimonials: { enabled: true },
    blog: { enabled: true, showRecent: true },
    contact: { enabled: true, showMap: true, showForm: true },
    footer: { enabled: true, showSocial: true }
  };

  useEffect(() => {
    // Initialize with defaults if nothing saved
    if (!siteContent) {
      setSiteContent(defaultSiteContent);
    }
    if (!layoutConfig) {
      setLayoutConfig(defaultLayoutConfig);
    }
  }, []);

  useEffect(() => {
    if (siteContent) {
      localStorage.setItem('naqd-site-content', JSON.stringify(siteContent));
    }
  }, [siteContent]);

  useEffect(() => {
    if (blogPosts !== null) {
      localStorage.setItem('naqd-blog-posts', JSON.stringify(blogPosts));
    }
  }, [blogPosts]);

  useEffect(() => {
    if (layoutConfig) {
      localStorage.setItem('naqd-layout-config', JSON.stringify(layoutConfig));
    }
  }, [layoutConfig]);

  useEffect(() => {
    localStorage.setItem('naqd-images', JSON.stringify(images));
  }, [images]);

  const updateContent = (section, key, value) => {
    setSiteContent(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [key]: value
      }
    }));
  };

  const updateLayout = (section, key, value) => {
    setLayoutConfig(prev => ({
      ...prev,
      [section]: {
        ...prev?.[section],
        [key]: value
      }
    }));
  };

  const addBlogPost = (post) => {
    setBlogPosts(prev => {
      const current = prev || blogPosts; // Use original as fallback
      const newPost = {
        ...post,
        id: Date.now(),
        slug: post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      };
      return [...current, newPost];
    });
  };

  const updateBlogPost = (id, updatedPost) => {
    setBlogPosts(prev => {
      const current = prev || blogPosts;
      return current.map(post => post.id === id ? { ...post, ...updatedPost } : post);
    });
  };

  const deleteBlogPost = (id) => {
    setBlogPosts(prev => {
      const current = prev || blogPosts;
      return current.filter(post => post.id !== id);
    });
  };

  const uploadImage = (key, base64Data) => {
    setImages(prev => ({
      ...prev,
      [key]: base64Data
    }));
  };

  const resetToDefaults = () => {
    localStorage.removeItem('naqd-site-content');
    localStorage.removeItem('naqd-blog-posts');
    localStorage.removeItem('naqd-layout-config');
    localStorage.removeItem('naqd-images');
    setSiteContent(defaultSiteContent);
    setBlogPosts(null); // Will use original data
    setLayoutConfig(defaultLayoutConfig);
    setImages({});
    window.location.reload();
  };

  const value = {
    siteContent: siteContent || defaultSiteContent,
    blogPosts: blogPosts || blogPosts, // Use original if none saved
    layoutConfig: layoutConfig || defaultLayoutConfig,
    images,
    updateContent,
    updateLayout,
    addBlogPost,
    updateBlogPost,
    deleteBlogPost,
    uploadImage,
    resetToDefaults
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};